import { Building2, Handshake, House, List, Users } from "lucide-react";
import { useGetList } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";

import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";

const NAV = [
  { to: "/", label: "Home", Icon: House, match: "/" },
  { to: "/contacts", label: "Contacts", Icon: Users, match: "/contacts/*" },
  {
    to: "/companies",
    label: "Companies",
    Icon: Building2,
    match: "/companies/*",
  },
  { to: "/deals", label: "Deals", Icon: Handshake, match: "/deals/*" },
];

/**
 * The app's left sidebar (W12 U1): the record lists, the tenant's saved views
 * with their counts, and at the foot the POC notice and the theme toggle.
 */
export function AppSidebar() {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const { pathname } = useLocation();
  const views = useGetList<{ id: string; name: string; result_count?: number }>(
    "saved_views",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "name", order: "ASC" },
    },
  );

  return (
    <Sidebar collapsible="icon" aria-label="Main">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/" className="no-underline">
                <img
                  className="[.light_&]:hidden size-6"
                  src={darkModeLogo}
                  alt=""
                />
                <img
                  className="[.dark_&]:hidden size-6"
                  src={lightModeLogo}
                  alt=""
                />
                <span className="truncate text-base font-semibold">
                  {title}
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <WorkspaceSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV.map(({ to, label, Icon, match }) => (
                <SidebarMenuItem key={to}>
                  <SidebarMenuButton
                    asChild
                    tooltip={label}
                    isActive={Boolean(matchPath(match, pathname))}
                  >
                    <Link to={to}>
                      <Icon />
                      <span>{label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>Saved views</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {(views.data ?? []).map((view) => (
                <SidebarMenuItem key={view.id}>
                  <SidebarMenuButton
                    asChild
                    tooltip={view.name}
                    isActive={pathname === `/views/${view.id}`}
                  >
                    <Link to={`/views/${view.id}`}>
                      <List />
                      <span>{view.name}</span>
                    </Link>
                  </SidebarMenuButton>
                  {view.result_count ? (
                    <SidebarMenuBadge>{view.result_count}</SidebarMenuBadge>
                  ) : null}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center">
          <p
            role="status"
            className="text-xs text-muted-foreground group-data-[collapsible=icon]:hidden"
          >
            This is a POC. Design is not frozen.
          </p>
          <ThemeModeToggle />
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
